import { useEffect, useState } from "react";
import SEO from "../components/SEO";
import ChapterHero from "../components/ChapterHero";
import NextPage from "../components/NextPage";
import SaltieShoutOutStationery from "../components/SaltieShoutOutStationery";
import SaltieShoutOutReveal from "../components/SaltieShoutOutReveal";
import SaltieShoutOutArchive from "../components/SaltieShoutOutArchive";
import { getSaltieShoutOut } from "../lib/api";

/**
 * Saltie Shout-Outs™ — Chapter 8.
 *
 * A quiet, warm chapter dedicated to celebrating the people who have
 * encouraged, inspired, supported, and helped this little dream grow.
 *
 * The featured letter content is admin-editable via
 * `GET /api/saltie-shout-out`. Clicking the stationery opens the
 * peaceful "shutter reveal" modal.
 */
export default function SaltieShoutOuts() {
  const [shoutOut, setShoutOut] = useState(null);
  const [revealOpen, setRevealOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getSaltieShoutOut()
      .then((d) => {
        if (!cancelled) setShoutOut(d || {});
      })
      .catch(() => {
        if (!cancelled) setShoutOut({});
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Convert snake_case backend fields to the stationery's camelCase props.
  const stationeryProps = shoutOut
    ? {
        recipient: shoutOut.recipient,
        body: shoutOut.body,
        photoUrl: shoutOut.photo_url || null,
        photoAlt: shoutOut.photo_alt || "",
        closingDate: shoutOut.closing_date || "",
      }
    : {};

  return (
    <div data-testid="saltie-shout-outs-page">      <SEO
        title="Saltie Shout-Outs™"
        description="At The Salty Moo™, no one builds a dream alone. Saltie Shout-Outs are handwritten notes celebrating the people who have made a difference along the way."
      />

      <ChapterHero
        path="/saltie-shout-outs"
        image="/assets/heroes/saltie-shout-outs.webp"
        title="Saltie Shout-Outs"
        titleAlt="Chapter 8 – Saltie Shout-Outs – Handwritten notes for the people who make a difference"
        subtitle="Handwritten notes for the people who make a difference."
        position="top-left"
      />

      <section
        className="max-w-3xl mx-auto text-center px-6 pt-16 pb-10 sm:pt-20 sm:pb-14"
        aria-labelledby="sso-intro-heading"
      >
        <p className="text-xs uppercase tracking-[0.32em] text-[#7E9268] mb-4">
          A quiet kind of gratitude
        </p>
        <h2
          id="sso-intro-heading"
          className="font-display text-[#4B3A2F]"
          style={{ fontSize: "clamp(1.9rem, 3.6vw, 2.8rem)", lineHeight: 1.15 }}
          data-testid="sso-intro-heading"
        >
          What is a Saltie Shout-Out<sup className="align-super">&trade;</sup>?
        </h2>
        <div className="gold-rule mx-auto my-6" style={{ width: "5rem" }} />
        <p
          className="font-body text-[#2C2724]/90 mx-auto"
          style={{ fontSize: "clamp(1rem, 1.4vw, 1.15rem)", lineHeight: 1.7, maxWidth: "38rem" }}
        >
          At The Salty Moo<sup className="align-super">&trade;</sup>, no one builds a dream alone.
          Saltie Shout-Outs<sup className="align-super">&trade;</sup> are our way of saying{" "}
          <em>thank you</em> to the people who have encouraged, inspired, supported, and helped this
          little dream grow.
        </p>
        <p
          className="font-body text-[#2C2724]/90 mx-auto mt-5"
          style={{ fontSize: "clamp(1rem, 1.4vw, 1.15rem)", lineHeight: 1.7, maxWidth: "38rem" }}
        >
          Every handwritten note is real.
          <br />
          Every story is personal.
          <br />
          Every person has made a difference.
        </p>

        <blockquote
          className="font-display italic text-[#4B3A2F] mt-10 mx-auto"
          style={{ fontSize: "clamp(1.15rem, 2vw, 1.55rem)", lineHeight: 1.5, maxWidth: "36rem" }}
        >
          &ldquo;Honor one another above yourselves.&rdquo;
          <br />
          <span className="not-italic text-[#7E9268] text-base tracking-[0.14em] uppercase">
            &mdash; Romans 12:10
          </span>
        </blockquote>

        <p
          className="font-script text-[#4B3A2F]/80 mt-10 mx-auto"
          style={{ fontSize: "clamp(1rem, 1.5vw, 1.25rem)", lineHeight: 1.5, maxWidth: "32rem" }}
          data-testid="sso-featured-hint"
        >
          Today&rsquo;s featured Saltie Shout-Out lives inside the shutter display above &mdash;
          and it reads like this&hellip;
        </p>
      </section>

      {/* Live stationery — clicking anywhere on the card opens the
          peaceful shutter reveal. */}
      <section
        className="max-w-3xl mx-auto px-6 pb-24"
        aria-labelledby="sso-stationery-label"
        data-testid="sso-stationery-section"
      >
        <p
          id="sso-stationery-label"
          className="text-xs uppercase tracking-[0.32em] text-[#7E9268] mb-6 text-center"
        >
          Today&rsquo;s Saltie Shout-Out
        </p>
        <button
          type="button"
          onClick={() => setRevealOpen(true)}
          aria-label="Open today's Saltie Shout-Out"
          data-testid="sso-open-reveal-btn"
          className="block w-full appearance-none bg-transparent border-0 p-0 cursor-pointer group focus:outline-none"
        >
          <div className="transition-transform duration-500 ease-out group-hover:-translate-y-1 group-focus:-translate-y-1">
            <SaltieShoutOutStationery {...stationeryProps} />
          </div>
          <p
            className="font-script text-[#7E9268] text-center mt-6"
            style={{ fontSize: "clamp(0.95rem, 1.3vw, 1.1rem)" }}
          >
            &mdash; click the letter to open &mdash;
          </p>
        </button>
        <p className="text-center mt-4">
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/saltie-shout-out/pdf`}
            data-testid="sso-download-featured-pdf"
            className="inline-block text-xs uppercase tracking-[0.32em] text-[#8B6F4E] hover:text-[#4B3A2F] transition-colors"
            style={{ letterSpacing: "0.32em" }}
          >
            &mdash;&nbsp;download the printable keepsake&nbsp;&mdash;
          </a>
        </p>
      </section>

      <SaltieShoutOutReveal
        open={revealOpen}
        onClose={() => setRevealOpen(false)}
        shoutOut={stationeryProps}
      />

      <SaltieShoutOutArchive />

      <NextPage to="/our-cow-munity" label="Turn to Our Cow-munity" />
    </div>
  );
}
